import {
  Card,
  HStack,
  IconChevronDown,
  IconLinkArrow,
  Text,
  VStack,
} from '@adc/parallax-component-library';
import { useFormikContext } from 'formik';
import React, { memo, useState } from 'react';
import { StudyCreateFormValues } from '../StudyCreate';
import { Subject } from '../../../models';
import PreventSubmitWrapper from '../../ui/forms/PreventSubmitWrapper';
import i18n from '../../../utilities/i18n';

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  gap: 'var(--size-xxs) var(--size-xs)',
};

const ValueWithLabel = ({
  label,
  value,
}: {
  label: string;
  value: string | number | boolean | undefined;
}) => {
  return (
    <>
      <Text type="$bodySmall.medium" textAlign="left">
        {label}
      </Text>
      <Text type="$bodySmall.default" textAlign="left">
        {value || '-'}
      </Text>
    </>
  );
};

const CollapsibleSubject = ({ subject }: { subject: Subject }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);

  return (
    <VStack>
      <HStack
        onPress={() => setIsCollapsed(!isCollapsed)}
        cursor="pointer"
        role="button"
      >
        <Text type="$bodySmall.strong">{subject.name}</Text>
        {isCollapsed ? <IconLinkArrow /> : <IconChevronDown />}
      </HStack>

      <div
        style={{
          ...gridStyle,
          display: isCollapsed ? 'none' : 'grid',
        }}
      >
        <ValueWithLabel
          label={i18n.t('Entity.description')}
          value={subject.description}
        />
        <ValueWithLabel
          label={i18n.t('Entity.deviceId')}
          value={subject.deviceId}
        />
      </div>
    </VStack>
  );
};

const TabReview = () => {
  const { values } = useFormikContext<StudyCreateFormValues>();

  return (
    <VStack
      height={'100%'}
      width={'100%'}
      gap="$2"
      backgroundColor="$neutral.0"
      padding="$2"
    >
      <PreventSubmitWrapper>
        <Text textAlign="left">
          {i18n.t('Page_StudyCreate.tab_review_instructions')}
        </Text>

        <Card
          title={values.name}
          titleTypeToken="$h4.strong"
          titleAriaLevel={3}
        >
          <div style={gridStyle}>
            <ValueWithLabel
              label={i18n.t('Entity.description')}
              value={values.description}
            />
            <ValueWithLabel label={i18n.t('Entity.type')} value={values.type} />

            <ValueWithLabel
              label={i18n.t('Entity.region')}
              value={values.region}
            />
          </div>

          <VStack gap="$2">
            {values.sites.map((site) => {
              return (
                <Card
                  key={site.key}
                  title={site.name}
                  titleTypeToken="$h4.strong"
                  titleAriaLevel={4}
                >
                  <div style={gridStyle}>
                    <ValueWithLabel
                      label={i18n.t('Entity.description')}
                      value={site.description}
                    />
                    <ValueWithLabel label="Region" value={site.region} />
                  </div>

                  {values.subjects
                    .filter((subject) => subject.siteKey === site.key)
                    .map((subject) => (
                      <CollapsibleSubject key={subject.key} subject={subject} />
                    ))}
                </Card>
              );
            })}
          </VStack>
        </Card>
      </PreventSubmitWrapper>
    </VStack>
  );
};

export default memo(TabReview);
